// -----------------------------------------------------------------------------
// AddProfileForm.jsx – UI harmonisée avec AddAppelOffreForm
// -----------------------------------------------------------------------------
import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { createProfile } from "../../redux/profilesSlice";
import CustomLoader from "../Common/CustomLoader";
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Divider,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  styled,
} from "@mui/material";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Check,
  Close as CloseIcon,
} from "@mui/icons-material";
import LocalisationAutocomplete from "./LocalisationAutocomplete";
import PhotoUpload from "./PhotoUpload";
import PhoneInput from "react-phone-input-2";
import MobiliteAutocomplete from "./MobiliteAutocomplete";
import "react-phone-input-2/lib/style.css";
import "./styles/AddProfileForm.css";

/* ────────────────────────────────────────────────────────────────────────── */
/*  🎨   DESIGN TOKENS  (identiques à AddAppelOffreForm)                     */
/* ────────────────────────────────────────────────────────────────────────── */
const NAVY = "#2F4E8B";
const NAVY_LIGHT = "#4a6bad";
const LINE_GREY = "#C6CDD6";
const LABEL_GREY = "#4F5565";
const OUTLINE = "#D4D9E0";
const SUCCESS = "#21D183";

/* ───  TextField arrondi, transitions douces                                */
const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    transition: "all .25s ease",
    "& fieldset": { borderColor: OUTLINE },
    "&:hover fieldset": { borderColor: NAVY_LIGHT },
    "&.Mui-focused fieldset": { borderColor: NAVY_LIGHT },
  },
  "& .MuiInputBase-input": { fontSize: 14 },
}));

/* ───  Fond dégradé                                                         */
const GradientPaper = ({ children, sx }) => (
  <Paper
    elevation={8}
    sx={{
      borderRadius: 3,
      p: { xs: 3, md: 4 },
      width: { xs: "100%", md: 900 },
      mx: "auto",
      background:
        "linear-gradient(135deg, #FFFFFF 0%, #F6F9FC 60%, #E9F0F9 100%)",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

/* ───  Connecteur stepper personnalisé                                      */
const ProfileConnector = styled(StepConnector)({
  top: 14,
  left: "calc(-50% + 16px)",
  right: "calc(50% + 16px)",
  "& .MuiStepConnector-line": {
    borderColor: LINE_GREY,
    borderTopWidth: 2,
    borderRadius: 1,
  },
});

/* ───  Icône de stepper                                                     */
const StepIconRoot = styled("div")(({ ownerState }) => ({
  zIndex: 1,
  width: 28,
  height: 28,
  borderRadius: "50%",
  border: `2px solid ${NAVY}`,
  backgroundColor:
    ownerState.completed ? NAVY : ownerState.active ? NAVY : "#FFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: ownerState.completed || ownerState.active ? "#FFF" : NAVY,
  fontWeight: 700,
  fontSize: 13,
}));

function StepIconComponent(props) {
  const { active, completed, icon } = props;
  return (
    <StepIconRoot ownerState={{ active, completed }}>
      {completed ? <Check fontSize="small" /> : icon}
    </StepIconRoot>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  ✔  VALIDATION YUP (inchangée)                                            */
/* ────────────────────────────────────────────────────────────────────────── */
const validationSchema = Yup.object().shape({
  nom: Yup.string().required("Le nom est requis."),
  poste: Yup.string().required("Le poste est requis."),
  tarif: Yup.number()
    .typeError("Le tarif doit être un nombre.")
    .required("Le tarif est requis.")
    .positive("Le tarif doit être positif."),
  localisation: Yup.string().required("La localisation est requise."),
  competences: Yup.string().required("Les compétences sont requises."),
  formations: Yup.string().required("Les formations sont requises."),
  certifications: Yup.string().nullable(),
  experiences: Yup.array()
    .of(
      Yup.object().shape({
        title: Yup.string().required("Le titre est requis."),
        company: Yup.string().required("L'entreprise est requise."),
        startDate: Yup.date().required("La date de début est requise."),
        current: Yup.boolean(),
        endDate: Yup.date()
          .nullable()
          .when("current", {
            is: false,
            then: (s) =>
              s.min(
                Yup.ref("startDate"),
                "Date de fin antérieure à la date de début."
              ),
            otherwise: (s) => s.nullable(),
          }),
        location: Yup.string().required("La localisation est requise."),
        description: Yup.string().required("La description est requise."),
      })
    )
    .min(1, "Au moins une expérience est requise.")
    .required("Les expériences sont requises."),
  linkedin_url: Yup.string()
    .url("URL LinkedIn invalide.")
    .required("L'URL LinkedIn est requise."),
  mode_travail: Yup.string().required("Le mode de travail est requis."),
  temps_travail: Yup.string().required("Le temps de travail est requis."),
  phone: Yup.string().required("Le téléphone est requis."),
  email: Yup.string().email("Email invalide.").required("L'email est requis."),
  disponibilite: Yup.boolean().oneOf(
    [true, false],
    "La disponibilité doit être cochée ou décochée."
  ),
  origine: Yup.string().required("L'origine est requise."),
  visibleContact: Yup.string()
    .oneOf(["phone", "email"], "Choix de contact invalide.")
    .required("Le contact visible est requis."),
  mobilite: Yup.array().of(Yup.string()).nullable(),
  summary: Yup.string().nullable(),
});

/* ───  Libellés du stepper (fr)                                             */
const steps = [
  "Informations Générales",
  "Compétences & Formations",
  "Préférences de Travail",
  "Expériences Professionnelles",
  "Télécharger CV",
];

/* ────────────────────────────────────────────────────────────────────────── */
/*  🖥️  COMPOSANT PRINCIPAL                                                 */
/* ────────────────────────────────────────────────────────────────────────── */
const AddProfileForm = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((s) => s.profiles);

  const [successMessage, setSuccessMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [cvFile, setCvFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imageError, setImageError] = useState("");
  const [visibleContact, setVisibleContact] = useState("phone");
  const [profileVisible, setProfileVisible] = useState(true);
  const [countryCode, setCountryCode] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [isFormDirty, setIsFormDirty] = useState(false);

  /* exposer dirty state */
  useImperativeHandle(ref, () => ({
    isFormDirty: () => isFormDirty,
    setIsFormDirty: (d) => setIsFormDirty(d),
  }));

  /* ───  Validation step‑par‑step                                           */
  const isStepValid = (values) => {
    switch (activeStep) {
      case 0:
        return (
          values.nom &&
          values.poste &&
          values.tarif &&
          values.localisation &&
          values.phone &&
          values.email
        );
      case 1:
        return values.competences && values.formations && values.linkedin_url;
      case 2:
        return values.mode_travail && values.temps_travail;
      case 3:
        return (
          values.experiences.length &&
          values.experiences.every(
            (e) =>
              e.title &&
              e.company &&
              e.startDate &&
              e.location &&
              e.description
          )
        );
      default:
        return true;
    }
  };

  /* ──────────────────────────────────────────────────────────────────────── */
  /*   📦  HANDLERS FICHIERS                                                 */
  /* ──────────────────────────────────────────────────────────────────────── */
  const handleImageChange = (e) => {
    const f = e.target.files[0];
    if (f && (f.size > 5 * 1024 * 1024 || !f.type.startsWith("image/"))) {
      setImageError("Image ≤ 5 Mo");
      setImageFile(null);
      return;
    }
    setImageError("");
    setImageFile(f);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.size > 5 * 1024 * 1024 || file.type !== "application/pdf")) {
      setFileError("PDF ≤ 5 Mo");
      setCvFile(null);
      return;
    }
    setFileError("");
    setCvFile(file);
  };

  /* ──────────────────────────────────────────────────────────────────────── */
  /*   🖌️  RENDER                                                           */
  /* ──────────────────────────────────────────────────────────────────────── */
  return (
    <Box
      onChange={() => setIsFormDirty(true)} // Mark form as dirty on any change
      sx={{ display: "flex", justifyContent: "center", p: 2 }}
    >
      <GradientPaper sx={{ position: "relative" }}>
        {/* (facultatif) vous pouvez ajouter un bouton de fermeture ici si modal */}
        {/* <IconButton ...><CloseIcon/></IconButton> */}

        {/* Titre */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: NAVY,
            mb: 3,
            textAlign: "center",
          }}
        >
          Ajouter un profil
        </Typography>

        {/* Upload photo */}
        <PhotoUpload
          imageFile={imageFile}
          handleImageChange={handleImageChange}
          imageError={imageError}
        />

        {/* Stepper */}
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          connector={<ProfileConnector />}
          sx={{
            mb: 4,
            "& .MuiStep-root": { px: { xs: 0.5, md: 2 } },
            "& .MuiStepLabel-label": {
              mt: 1,
              fontSize: 14,
              fontWeight: 600,
              color: LABEL_GREY,
            },
            "& .MuiStepLabel-label.Mui-completed": { color: NAVY },
            "& .MuiStepLabel-label.Mui-active": { color: NAVY },
          }}
        >
          {steps.map((label, i) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={StepIconComponent}
                StepIconProps={{ icon: i < 9 ? `0${i + 1}` : i + 1 }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Formik */}
        <Formik
          initialValues={{
            nom: "",
            poste: "",
            tarif: "",
            annees_experience: "",
            localisation: "",
            competences: "",
            formations: "",
            certifications: "",
            experiences: [
              {
                title: "",
                company: "",
                startDate: "",
                endDate: "",
                current: false,
                location: "",
                description: "",
              },
            ],
            linkedin_url: "",
            mode_travail: "",
            temps_travail: "",
            phone: "",
            email: "",
            disponibilite: false,
            origine: "interne",
            visibleContact: "phone",
            mobilite: [],
            summary: "",
          }}
          validationSchema={validationSchema}
          validateOnMount
          onSubmit={async (vals, { resetForm, setFieldError }) => {
            setInfoMessage(
              "Veuillez patienter pendant la création de votre compte..."
            );
            const values = { ...vals };
            values.experiences = values.experiences.map((e) =>
              e.current ? { ...e, endDate: null } : e
            );
            values.visibleContact = visibleContact;
            values.phone = `${countryCode}${values.phone}`;

            try {
              await dispatch(
                createProfile({
                  profileData: { ...values, profileVisible },
                  cvFile,
                  imageFile,
                })
              ).unwrap();
              setSuccessMessage("Profil ajouté avec succès !");
              resetForm();
              setCvFile(null);
              setImageFile(null);
              setTimeout(() => setSuccessMessage(""), 5000);
              setIsFormDirty(false);
            } catch (err) {
              if (err.message === "Email already exists.") {
                setFieldError("email", "Cet email est déjà utilisé.");
              }
            } finally {
              setInfoMessage("");
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            submitForm,
            isSubmitting,
          }) => (
            <Form onChange={() => setIsFormDirty(true)}>
              {/* ─── ÉTAPE 0 : Infos générales ───────────────────────────── */}
              {activeStep === 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: NAVY, mb: 2, fontWeight: 700 }}
                  >
                    Informations Générales
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={CustomTextField}
                        fullWidth
                        id="nom"
                        name="nom"
                        label="Nom"
                        error={touched.nom && Boolean(errors.nom)}
                        helperText={touched.nom && errors.nom}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={CustomTextField}
                        fullWidth
                        id="poste"
                        name="poste"
                        label="Poste"
                        error={touched.poste && Boolean(errors.poste)}
                        helperText={touched.poste && errors.poste}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={CustomTextField}
                        fullWidth
                        type="number"
                        id="tarif"
                        name="tarif"
                        label="Tarif (€)"
                        inputProps={{ min: 0 }}
                        error={touched.tarif && Boolean(errors.tarif)}
                        helperText={touched.tarif && errors.tarif}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <LocalisationAutocomplete
                        value={values.localisation}
                        onChange={(v) => setFieldValue("localisation", v)}
                      />
                      {touched.localisation && errors.localisation && (
                        <Typography variant="caption" color="error">
                          {errors.localisation}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <PhoneInput
                        country="fr"
                        value={values.phone}
                        onChange={(val, c) => {
                          setFieldValue("phone", val);
                          setCountryCode(c.dialCode);
                        }}
                        buttonStyle={{
                          background:
                            "linear-gradient(135deg, #FFFFFF 0%, #E9F0F9 100%)",
                          borderRadius: "8px 0 0 8px",
                        }}
                        inputStyle={{
                          width: "100%",
                          background: "transparent",
                          borderRadius: 8,
                          borderColor: OUTLINE,
                          height: 56,
                        }}
                        inputProps={{ name: "phone", required: true }}
                      />
                      {touched.phone && errors.phone && (
                        <Typography variant="caption" color="error">
                          {errors.phone}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel id="visible-contact-label">
                          Contact visible
                        </InputLabel>
                        <Select
                          labelId="visible-contact-label"
                          value={visibleContact}
                          label="Contact visible"
                          onChange={(e) => {
                            setVisibleContact(e.target.value);
                            setFieldValue("visibleContact", e.target.value);
                          }}
                        >
                          <MenuItem value="phone">Phone</MenuItem>
                          <MenuItem value="email">Email</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Field
                        as={CustomTextField}
                        fullWidth
                        id="email"
                        name="email"
                        label="Email"
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* ─── ÉTAPE 1 : Compétences & Formations ──────────────────── */}
              {activeStep === 1 && (
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: NAVY, mb: 2, fontWeight: 700 }}
                  >
                    Compétences & Formations
                  </Typography>
                  <Grid container spacing={3}>
                    {[
                      {
                        id: "competences",
                        label: "Compétences (séparées par des virgules)",
                      },
                      {
                        id: "formations",
                        label: "Formations (séparées par des virgules)",
                      },
                      {
                        id: "certifications",
                        label:
                          "Certifications (séparées par des virgules) — facultatif",
                      },
                      { id: "linkedin_url", label: "URL LinkedIn" },
                    ].map(({ id, label }) => (
                      <Grid item xs={12} sm={6} key={id}>
                        <Field
                          as={CustomTextField}
                          fullWidth
                          id={id}
                          name={id}
                          label={label}
                          error={touched[id] && Boolean(errors[id])}
                          helperText={touched[id] && errors[id]}
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Field
                        as={CustomTextField}
                        fullWidth
                        multiline
                        minRows={3}
                        id="summary"
                        name="summary"
                        label="Résumé"
                        error={touched.summary && Boolean(errors.summary)}
                        helperText={touched.summary && errors.summary}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* ─── ÉTAPE 2 : Préférences ──────────────────────────────── */}
              {activeStep === 2 && (
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: NAVY, mb: 2, fontWeight: 700 }}
                  >
                    Préférences de Travail
                  </Typography>
                  <Grid container spacing={3}>
                    {[
                      {
                        id: "mode_travail",
                        label: "Mode de Travail",
                        items: ["Flexible", "Remote", "Sur Site"],
                      },
                      {
                        id: "temps_travail",
                        label: "Temps de Travail",
                        items: ["Temps partiel", "Temps plein"],
                      },
                    ].map(({ id, label, items }) => (
                      <Grid item xs={12} sm={6} key={id}>
                        <FormControl fullWidth>
                          <InputLabel id={`${id}-label`}>{label}</InputLabel>
                          <Select
                            labelId={`${id}-label`}
                            id={id}
                            name={id}
                            value={values[id]}
                            label={label}
                            onChange={handleChange}
                            error={touched[id] && Boolean(errors[id])}
                          >
                            <MenuItem value="">
                              <em>Sélectionner</em>
                            </MenuItem>
                            {items.map((it) => (
                              <MenuItem key={it} value={it}>
                                {it}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched[id] && errors[id] && (
                            <Typography variant="caption" color="error">
                              {errors[id]}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    ))}

                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        justifyContent="center"
                        gap={4}
                        alignItems="center"
                      >
                        {[
                          {
                            id: "disponibilite",
                            label: "Disponible",
                            checked: values.disponibilite,
                            onChange: (e) =>
                              setFieldValue("disponibilite", e.target.checked),
                          },
                          {
                            id: "profileVisible",
                            label: "Profil Visible",
                            checked: profileVisible,
                            onChange: (e) => setProfileVisible(e.target.checked),
                          },
                        ].map((c) => (
                          <FormControlLabel
                            key={c.id}
                            control={
                              <Checkbox
                                checked={c.checked}
                                onChange={c.onChange}
                                sx={{
                                  color: NAVY,
                                  "&.Mui-checked": { color: NAVY },
                                }}
                              />
                            }
                            label={
                              <Typography
                                sx={{ fontWeight: 700, color: NAVY }}
                              >
                                {c.label}
                              </Typography>
                            }
                          />
                        ))}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: "1.5rem",
                          textAlign: "center",
                          color: NAVY,
                          mb: 2,
                          fontWeight: 700,
                        }}
                      >
                        Mobilité
                      </Typography>
                      <MobiliteAutocomplete
                        value={values.mobilite}
                        onChange={(v) => setFieldValue("mobilite", v)}
                      />
                      {touched.mobilite && errors.mobilite && (
                        <Typography variant="caption" color="error">
                          {errors.mobilite}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* ─── ÉTAPE 3 : Expériences ──────────────────────────────── */}
              {activeStep === 3 && (
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: NAVY, mb: 2, fontWeight: 700 }}
                  >
                    Expériences Professionnelles
                  </Typography>

                  {/* bouton ajout */}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() =>
                        setFieldValue("experiences", [
                          ...values.experiences,
                          {
                            title: "",
                            company: "",
                            startDate: "",
                            endDate: "",
                            current: false,
                            location: "",
                            description: "",
                          },
                        ])
                      }
                      sx={{ backgroundColor: NAVY, fontWeight: "bold" }}
                    >
                      Ajouter
                    </Button>
                  </Box>

                  <FieldArray name="experiences">
                    {({ remove }) => (
                      <>
                        {values.experiences.map((exp, idx) => (
                          <Paper
                            key={idx}
                            variant="outlined"
                            sx={{
                              p: 3,
                              mb: 3,
                              borderRadius: 2,
                              background:
                                "linear-gradient(135deg, #FFFFFF 0%, #F6F9FC 60%, #E9F0F9 100%)",
                              boxShadow: "0px 4px 20px rgba(0,0,0,.1)",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 2,
                              }}
                            >
                              <Typography variant="subtitle1">
                                Expérience {idx + 1}
                              </Typography>
                              <IconButton
                                color="error"
                                onClick={() => remove(idx)}
                                disabled={values.experiences.length === 1}
                              >
                                <RemoveIcon />
                              </IconButton>
                            </Box>

                            <Grid container spacing={3}>
                              {[
                                { id: "title", label: "Titre" },
                                { id: "company", label: "Entreprise" },
                              ].map(({ id, label }) => (
                                <Grid item xs={12} sm={6} key={id}>
                                  <Field
                                    as={CustomTextField}
                                    fullWidth
                                    id={`experiences.${idx}.${id}`}
                                    name={`experiences.${idx}.${id}`}
                                    label={label}
                                    error={
                                      touched.experiences?.[idx]?.[id] &&
                                      Boolean(errors.experiences?.[idx]?.[id])
                                    }
                                    helperText={
                                      touched.experiences?.[idx]?.[id] &&
                                      errors.experiences?.[idx]?.[id]
                                    }
                                  />
                                </Grid>
                              ))}

                              <Grid item xs={12} sm={6}>
                                <Field
                                  as={CustomTextField}
                                  fullWidth
                                  type="date"
                                  id={`experiences.${idx}.startDate`}
                                  name={`experiences.${idx}.startDate`}
                                  label="Date de début"
                                  InputLabelProps={{ shrink: true }}
                                  error={
                                    touched.experiences?.[idx]?.startDate &&
                                    Boolean(
                                      errors.experiences?.[idx]?.startDate
                                    )
                                  }
                                  helperText={
                                    touched.experiences?.[idx]?.startDate &&
                                    errors.experiences?.[idx]?.startDate
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Field
                                  as={CustomTextField}
                                  fullWidth
                                  type="date"
                                  id={`experiences.${idx}.endDate`}
                                  name={`experiences.${idx}.endDate`}
                                  label="Date de fin"
                                  disabled={exp.current}
                                  InputLabelProps={{ shrink: true }}
                                  error={
                                    touched.experiences?.[idx]?.endDate &&
                                    Boolean(errors.experiences?.[idx]?.endDate)
                                  }
                                  helperText={
                                    touched.experiences?.[idx]?.endDate &&
                                    errors.experiences?.[idx]?.endDate
                                  }
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={exp.current}
                                      onChange={(e) => {
                                        setFieldValue(
                                          `experiences.${idx}.current`,
                                          e.target.checked
                                        );
                                        if (e.target.checked) {
                                          setFieldValue(
                                            `experiences.${idx}.endDate`,
                                            ""
                                          );
                                        }
                                      }}
                                      sx={{
                                        color: NAVY,
                                        "&.Mui-checked": { color: NAVY },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      sx={{ fontWeight: 700, color: NAVY }}
                                    >
                                      Je travaille actuellement ici
                                    </Typography>
                                  }
                                />
                              </Grid>

                              <Grid item xs={12} sm={6} md={4}>
                                <LocalisationAutocomplete
                                  value={exp.location}
                                  onChange={(v) =>
                                    setFieldValue(
                                      `experiences.${idx}.location`,
                                      v
                                    )
                                  }
                                />
                                {touched.experiences?.[idx]?.location &&
                                  errors.experiences?.[idx]?.location && (
                                    <Typography
                                      variant="caption"
                                      color="error"
                                    >
                                      {errors.experiences[idx].location}
                                    </Typography>
                                  )}
                              </Grid>

                              <Grid item xs={12}>
                                <Field
                                  as={CustomTextField}
                                  fullWidth
                                  multiline
                                  minRows={3}
                                  id={`experiences.${idx}.description`}
                                  name={`experiences.${idx}.description`}
                                  label="Description"
                                  error={
                                    touched.experiences?.[idx]?.description &&
                                    Boolean(
                                      errors.experiences?.[idx]?.description
                                    )
                                  }
                                  helperText={
                                    touched.experiences?.[idx]?.description &&
                                    errors.experiences?.[idx]?.description
                                  }
                                />
                              </Grid>
                            </Grid>
                          </Paper>
                        ))}
                      </>
                    )}
                  </FieldArray>
                </Box>
              )}

              {/* ─── ÉTAPE 4 : CV ───────────────────────────────────────── */}
              {activeStep === 4 && (
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: NAVY, mb: 2, fontWeight: 700 }}
                  >
                    Télécharger CV (optionnel)
                  </Typography>
                  <Typography variant="body2" sx={{ color: "gray", mb: 1 }}>
                    PDF – 5 Mo max.
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    sx={{ background: NAVY, mb: 2, fontWeight: "bold" }}
                  >
                    Choisir un fichier
                    <input type="file" hidden onChange={handleFileChange} />
                  </Button>
                  {cvFile && (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {cvFile.name}
                    </Typography>
                  )}
                  {fileError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {fileError}
                    </Alert>
                  )}
                </Box>
              )}

              {/* ─── Messages globaux ───────────────────────────────────── */}
              {error && error !== "Email already exists." && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Une erreur s'est produite.
                </Alert>
              )}
              {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {successMessage}
                </Alert>
              )}
              {infoMessage && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {infoMessage}
                </Alert>
              )}

              {/* ─── Navigation ─────────────────────────────────────────── */}
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
                {activeStep > 0 ? (
                  <Button
                    variant="outlined"
                    onClick={() => setActiveStep((s) => s - 1)}
                    sx={{
                      borderColor: OUTLINE,
                      color: LABEL_GREY,
                      borderRadius: 1,
                      textTransform: "none",
                      px: 4,
                      fontWeight: "bold",
                      "&:hover": {
                        backgroundColor: OUTLINE,
                        color: NAVY,
                      },
                    }}
                  >
                    Précédent
                  </Button>
                ) : (
                  <Box /> // pour alignement
                )}

                <Button
                  variant="contained"
                  onClick={() =>
                    activeStep === steps.length - 1
                      ? submitForm()
                      : setActiveStep((s) => s + 1)
                  }
                  disabled={loading || isSubmitting || !isStepValid(values)}
                  sx={{
                    backgroundColor: NAVY,
                    borderRadius: 1,
                    textTransform: "none",
                    px: 5,
                    fontWeight: "bold",
                    "&:hover": { backgroundColor: NAVY_LIGHT },
                    height: 42,
                  }}
                >
                  {loading ? (
                    <CustomLoader size={22} sx={{ color: "#FFF" }} />
                  ) : activeStep === steps.length - 1 ? (
                    "Enregistrer"
                  ) : (
                    "Suivant"
                  )}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </GradientPaper>
    </Box>
  );
});

export default AddProfileForm;
